import { useEffect, useRef } from 'react'

import { getApiTokens } from '../../hooks/useApi'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import {
    useContextsStore,
    selectContextsAreViewersOwn,
} from '../../stores/useContextsStore'
import { useOnboardingStore } from '../../stores/useOnboardingStore'
import { STAGES, getStageFlow } from './copy'
import { trackOnboarding, markGuidedUser } from './trackOnboarding'

const NEW_ACCOUNT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

// A session cached before the server sent createdAt reads as not new, which
// leaves only the no-graphs rule.
const isNewAccount = createdAt =>
    !!createdAt &&
    Date.now() - new Date(createdAt).getTime() < NEW_ACCOUNT_WINDOW_MS

const countApiKeys = async userId => {
    const tokens = await getApiTokens(userId)
    return Array.isArray(tokens) ? tokens.filter(Boolean).length : 0
}

/**
 * Decides whether the guide runs for this viewer and when it ends: authenticated
 * + (zero graphs, or an account under a week old) to start — the second clause
 * catches agent users whose first graph was built over MCP before they ever
 * opened the app; a key once the loop is under way, or arriving at the closing
 * step from an earlier visit, to end. `contexts` belongs to the DISPLAYED owner,
 * so only `selectContextsAreViewersOwn` may gate on it.
 */
export default function useGuideEligibility() {
    const user = useBootstrapStore(state => state.user)
    const loadedContextList = useContextsStore(state => state.loadedContextList)
    const contextsCount = useContextsStore(state => state.contexts.length)
    const contextsAreViewersOwn = useContextsStore(selectContextsAreViewersOwn)

    const status = useOnboardingStore(state => state.status)
    const stage = useOnboardingStore(state => state.stage)
    const persona = useOnboardingStore(state => state.persona)
    const startedWithKey = useOnboardingStore(state => state.startedWithKey)
    const storageUsername = useOnboardingStore(state => state.storageUsername)
    const advancedThisSession = useOnboardingStore(
        state => state.advancedThisSession
    )
    const hydrateForUser = useOnboardingStore(state => state.hydrateForUser)
    const activate = useOnboardingStore(state => state.activate)
    const markIneligible = useOnboardingStore(state => state.markIneligible)
    const complete = useOnboardingStore(state => state.complete)

    const isThisUsersStore =
        !!user?.isAuthenticated && storageUsername === user.username
    const viewersContextsLoaded = loadedContextList && contextsAreViewersOwn

    // Keyed per username so accounts sharing a browser can't inherit each other's verdict.
    useEffect(() => {
        if (user?.isAuthenticated && user.username) {
            hydrateForUser(user.username)
        }
    }, [user, hydrateForUser])

    // Anonymous viewers never hydrate and stay 'unknown'.
    useEffect(() => {
        if (!isThisUsersStore || status !== 'unknown') return
        if (!viewersContextsLoaded) return
        const newAccount = isNewAccount(user.createdAt)
        if (contextsCount > 0 && !newAccount) {
            markIneligible()
            return
        }
        let cancelled = false
        const start = hadApiKey => {
            if (cancelled) return
            activate({ startedWithKey: hadApiKey })
            markGuidedUser()
            trackOnboarding('started', {
                stage: STAGES.BRING,
                step: 1,
                had_graphs: contextsCount > 0,
                had_api_key: hadApiKey,
                new_account: newAccount,
            })
        }
        // An unreadable key list assumes a key: the guide then ends on its own
        // last step rather than closing on a key that may predate it.
        countApiKeys(user.id)
            .then(keyCount => start(keyCount > 0))
            .catch(() => start(true))
        return () => {
            cancelled = true
        }
    }, [
        isThisUsersStore,
        status,
        viewersContextsLoaded,
        contextsCount,
        user,
        activate,
        markIneligible,
    ])

    // Arriving at the closing step without advancing this session means an earlier visit
    // finished the loop; re-greeting on every load is noise.
    const completionCheckedRef = useRef(false)
    useEffect(() => {
        if (!isThisUsersStore || status !== 'active') return
        if (!viewersContextsLoaded || contextsCount === 0) return

        if (!advancedThisSession && stage === STAGES.EXPLORE) {
            complete()
            return
        }
        // A graph plus a key closes the default loop; a flow that connects
        // first has both by its second step and must finish on its own, and so
        // must a user whose key predates the guide (an agent user over MCP).
        if (getStageFlow(persona).includes(STAGES.CONNECT)) return
        if (startedWithKey) return
        if (completionCheckedRef.current) return
        completionCheckedRef.current = true
        let cancelled = false
        countApiKeys(user.id)
            .then(keyCount => {
                if (!cancelled && keyCount > 0) complete()
            })
            .catch(() => {
                // Leave it; the next load checks again.
            })
        return () => {
            cancelled = true
        }
    }, [
        isThisUsersStore,
        status,
        viewersContextsLoaded,
        contextsCount,
        stage,
        persona,
        startedWithKey,
        advancedThisSession,
        user,
        complete,
    ])
}
